// @ts-check

import {
  convertDashboardFilterTagListToParsedList,
  mergeFiltersToParams,
  mergeFiltersToParamsWithoutClear,
} from './format/dashboardFilter';
import {
  DashboardFolder,
  DashboardWidget,
  DashboardFEFilterTagList,
  DashboardView,
  ObsoleteAdAccount,
} from './types/dashboard.types';
import { getParsedDateFilter, getTimezone } from './format/dashboardDateFilter';
import * as R from 'ramda';
import {
  CHART_TYPES,
  CHART_TYPES_FOR_GET_TABLE,
  checkSourceTableMode,
  selectDataSources,
  selectedDimensionId,
} from './format/dashboardDataSources';
const { WIDGET_TYPE } = require('../dashboardView/constants');
const { CREATIVE_INSIGHT_FILED_BY_BREAKDOWN } = require('./constants');
const rax = require('retry-axios');
const Bluebird = require('bluebird');
const _ = require('lodash');
const memoize = require('memoizee');
const moment = require('moment');
const { DashboardClient } = require('./dashboardClient');
const resultIndicatorUtils = require('./utils/resultIndicatorUtils');
const DATE_BREAKDOWNS = ['day', 'week', 'weekSunSat', 'weekNumber', 'nikeWeekNumber', 'month', 'autoTime'];
const { filterAdAccounts } = require('./table');
const { _addRelativeDateRangeInfoIfLegacy } = require('./utils/dateUtils');
const { unionTables } = require('./aggregateTable');
const { getMetricsFromWidget } = require('./utils/widgetUtils');
const { getRelatedAnalyticsMetrics } = require('./utils/metricUtils');
const blendDataUtils = require('./utils/blendDataUtils');
const convertFormat = require('./convertFormat');
const { v4 } = require('uuid');
const { queryReRenderCallBack, queryFailCallBack, queryDoneCallBack } = convertFormat;
const { getAnalyticsInfo, getDataSourceFields } = require('./analyticsConfig');
const marketingLevels = ['adAccount', 'campaign', 'adSet', 'ad', 'keyword'];
const dateGroupBreakdowns = ['day', 'week', 'weekSunSat', 'weekNumber', 'nikeWeekNumber', 'month', 'autoTime'];

const isRequestCancelled = (error: unknown): boolean => {
  if (!error) return false;
  const maybeError = error as { code?: string; name?: string; message?: string };
  return (
    maybeError?.code === 'ERR_CANCELED' || maybeError?.name === 'CanceledError' || maybeError?.message === 'canceled'
  );
};

function getAutoDateGroup({ from, to }) {
  const duration = moment(to).diff(from, 'days');
  let dateGroup;
  if (duration < 90) {
    dateGroup = 'day';
  } else if (duration < 365) {
    dateGroup = 'week';
  } else {
    dateGroup = 'month';
  }
  return dateGroup;
}

Bluebird.config({
  cancellation: true,
});

/**
 * @typedef {import("./architecture/AdAccountArchitecture")} AdAccountArchitecture
 * @typedef {import("./architecture/AdrielCampaign")} AdrielCampaign
 * @typedef {import("axios").AxiosInstance} Axios
 * @typedef {import("../types").DashboardTable} DashboardTable
 * @typedef {import("../types").DashboardTableRow} DashboardTableRow
 * @typedef {import("../types").DashboardFilter} DashboardFilter
 * @typedef {import("../types").DateFilter} DateFilter
 * @typedef {import("../types").ConnectAdAccount} ConnectAdAccount
 */

// @ts-ignore
const isBrowser = typeof window !== 'undefined';
// @ts-ignore
const unionWorkerCount = (isBrowser && Math.floor((navigator?.hardwareConcurrency || 0) / 2)) || 5; // this is the number of cores, if accessible by the browser.
let unionWorkerIndex = 0;
/** @type {Worker[]} workerList */
const unionWorkerList = new Array(unionWorkerCount);

const NOOP = _.noop;

const notUsedKeys = [
  // searchFilter and accFilters were originally commented out, but then they popped up again so I added them back to notUsedKeys list.
  // I can not see them being used in the common project
  'searchFilter',
  'accFilters',

  'currency',
  'lastRefreshedTime',
  'jobIds',
  'currentViewId',
  'viewId',
  'isComparison',
  'forceRefreshCache',
  'metricFilter',
  // 'customMetrics',

  // used in new WT to not fetch too often
  'retryCount',
  // Ignore for now, not yet implemented, but we must handle it later
];

const RETRY_AXIOS_OPTION = Object.freeze({
  validateStatus: () => true,
  raxConfig: {
    retry: 2,
    retryDelay: 100,
    httpMethodsToRetry: ['POST'],
    statusCodesToRetry: [
      [504, 504],
      [524, 524],
    ],
  },
});
export class DashboardQueryClient extends DashboardClient {
  /**
   *
   * @param {object} p
   * @param {string} p.apiUrl Url of the api, eg: 'https://app.adriel.com/api
   * @param {string} p.accountId (optional) Account Id displayed on the dashboard. If not provided, it will be found in the session. This option is useful for users with multiple workspaces and back officers looking at someone else dashboard.
   * @param {string} p.cookie  (optional) Cookie to be send. Only useful for development, and mobile app.
   * @param {string} p.token  (optional) JWT Header token for mobile app.
   * @param {string} p.currency  (optional) currency
   * @param {boolean} [p.useWorker] Use WebWorker or not. Default true.
   * @param {string} [p.viewKey] (optional) Use viewKey to access restricted route
   * @param {boolean} [p.useWidgetTable] (optional) Use WidgetTable route
   */
  constructor({
    callQuery = null,
    getAdAccounts = null,
    getCustomMetrics = null,
    onlyCache = false,
    useWidgetTable = false,
    buildMode = false,
  }) {
    super(arguments[0]);
    this.mode = 'query';
    this.onlyCache = onlyCache;
    this.useWidgetTable = useWidgetTable;
    this.buildMode = buildMode;
    this.memoizedCallQuery = this.noCache
      ? this.callQuery
      : memoize(callQuery ?? this.callQuery, {
          normalizer: args => JSON.stringify(args),
          maxAge: 10 * 60 * 1000, // 10 minutes
          max: 1000,
          promise: true,
        });
    this.memoizedGetAdAccounts = this.noCache
      ? this.getAdAccounts
      : memoize(getAdAccounts ?? this.getAdAccounts, {
          normalizer: args => JSON.stringify(args),
          maxAge: 1 * 60 * 1000, // 1 minutes
          max: 1000,
          promise: true,
        });
    this.memoizedGetCustomMetrics = this.noCache
      ? this.getCustomMetrics
      : memoize(getCustomMetrics ?? this.getCustomMetrics, {
          normalizer: args => JSON.stringify(args),
          maxAge: 1 * 60 * 1000, // 1 minutes
          max: 1000,
          promise: true,
        });
  }

  getAccountSets({
    adAccounts,
    useAllAdAccounts = false,
    isCustomTable = false,
    metrics = [],
    noWidgetPassed = false,
    dimensionId = undefined,
  }) {
    let analyticAccounts = adAccounts.filter(adAccount => adAccount.is_analytic);
    let customReportAccounts = adAccounts.filter(
      adAccount => adAccount.isCustomReportAccount && !adAccount.is_analytic,
    );
    if (isCustomTable) {
      adAccounts = customReportAccounts.concat(analyticAccounts);
      analyticAccounts = [];
      return adAccounts.map(adAccount => ({ adAccount }));
    } else {
      adAccounts = adAccounts.filter(adAccount => !adAccount.is_analytic && !adAccount.isCustomReportAccount);
    }
    if (['creativeWord'].includes(dimensionId)) {
      return [...adAccounts.map(adAccount => ({ adAccount }))];
    }
    if (['colorClustering'].includes(dimensionId)) {
      return [...adAccounts.map(adAccount => ({ adAccount }))];
    }
    if (['emojiAnalysis'].includes(dimensionId)) {
      return [...adAccounts.map(adAccount => ({ adAccount }))];
    }
    let accountSets = [
      ...adAccounts.map(adAccount => ({ adAccount })),
      // ...customReportAccounts.filter(adAccount => adAccount.settings).map(adAccount => ({ adAccount })),
      ...customReportAccounts.map(adAccount => ({ adAccount })),
      ...analyticAccounts
        .map(analyticAccount => {
          if (analyticAccount.channel == 'googleAnalytics') {
            const relatedMetrics = getRelatedAnalyticsMetrics({ analyticAccount, metrics });
            if (relatedMetrics.length > 0 || noWidgetPassed) {
              return {
                adAccounts: useAllAdAccounts ? null : adAccounts,
                adAccount: analyticAccount,
                metrics: relatedMetrics,
              };
            }
          } else {
            return { adAccounts: useAllAdAccounts ? null : adAccounts, adAccount: analyticAccount };
          }
        })
        .flat()
        .filter(Boolean),
    ];
    return accountSets;
  }

  async callQuery({ body, params }) {
    const baseRoute = 'dashboard/dataSource/query';
    const joinedDataSourceName = body?.queries[0]?.joinedQueries?.[0].query?.dataSources?.[0]?.connectorName;
    let connectorName = body.useCache?.dataSource ? 'cache' : body?.queries[0].dataSources?.[0]?.connectorName || '';
    if (joinedDataSourceName) {
      connectorName = joinedDataSourceName;
    }
    this; // <-- I have no idea why, but removing this line leads to an error "typeError: this.retryAxios is undefined."
    const result = await this.retryAxios.post(`${baseRoute}/${connectorName}/`, body, {
      params,
      raxConfig: {
        retry: 2,
        retryDelay: 100,
        httpMethodsToRetry: ['POST'],
        statusCodesToRetry: [[524, 524]],
        shouldRetry: err => {
          const cfg = rax.getConfig(err);
          if (!cfg) return false; // retry metadata missing, bail out
          const { currentRetryAttempt = 0, retry = 0 } = cfg;
          if (currentRetryAttempt >= retry) return false; // ensure max retries is always respected
          return rax.shouldRetryRequest(err) && connectorName === 'kakao';
        },
      },
    });
    return result.data;
  }

  async query({ workspaceId, currency, queries, useCache = true, view = null }) {
    if (queries.length === 0) return [];
    const { breakdowns, when, dataSources, joinedQueries, mainDataSource } = queries[0];
    const connectorNameParam = mainDataSource ? mainDataSource.connectorName : 'all';
    const whenParam = `${when.from}_to_${when.to}${when.groupBy === 'all' ? '' : '_' + when.groupBy}`;
    return this.memoizedCallQuery({
      // return this.callQuery({
      body: {
        workspaceId,
        currency,
        queries,
        useCache: { dataSource: useCache, row: this.useRowCache ? 'cacheOrFetch' : 'fetchOnly' },
        viewId: view ? view.id : undefined,
      },
      params: {
        c: `${breakdowns.join('/')}-${connectorNameParam}-${whenParam}${useCache ? '-cache' : ''}`,
      },
    });
  }

  getQueryId(query) {
    let id = query.dataSources.map(dataSource => dataSource.dataSourceId).join('-');
    if (query.joinedQueries) {
      id += '-' + query.joinedQueries.flatMap(({ query }) => query.dataSources.map(d => d.dataSourceId)).join('-');
    }
    id += `_${query.when.from}_${query.when.to}`;
    return id;
  }

  getRawFieldByField({ fields, dataSource }) {
    const analyticInfo = getAnalyticsInfo(dataSource);
    const analyticFieldToField = fields.reduce((acc, analyticField) => {
      acc[analyticField] = analyticInfo.getAnalyticsFieldToField(analyticField, dataSource);
      return acc;
    }, {});
    return analyticFieldToField;
  }

  getQueries({ queryRequest, dataSources, isCustomTable, isGAAutoMatchingEnabled, withTotal }) {
    let queries = [];
    const dataSourcesByType = _.groupBy(dataSources, 'dataSourceType');
    if (this.debug) {
      console.log('dataSourcesByType', dataSourcesByType);
    }
    const analyticDataSources = dataSourcesByType['analytic'] || [];
    const marketingDataSources = dataSourcesByType['marketing'] || [];
    const customDataSources = dataSourcesByType['normal'] || [];
    const marketingWhere = [];
    const gaWhere = [];
    const ga4Where = [];
    for (const condition of queryRequest.where) {
      if (condition.field.startsWith('ga:')) {
        gaWhere.push(condition);
      } else if (condition.field.startsWith('ga4:')) {
        ga4Where.push(condition);
      } else {
        marketingWhere.push(condition);
      }
    }
    if (isCustomTable) {
      queries = [...analyticDataSources, ...customDataSources].map(dataSource => {
        let where = null;
        if (dataSource.type === 'googleAnalytics') {
          where = gaWhere;
        } else if (dataSource.type === 'googleAnalytics4') {
          where = ga4Where;
        }
        return {
          ...queryRequest,
          where,
          mainDataSource: dataSource,
          table: 'custom',
          dataSources: [dataSource],
        };
      });
    } else {
      const breakdown = queryRequest.breakdowns[0];
      let level = breakdown;
      if (!marketingLevels.includes(level)) {
        if (Object.keys(CREATIVE_INSIGHT_FILED_BY_BREAKDOWN).includes(level)) {
          level = 'ad';
        } else {
          level = 'adAccount';
        }
      }
      const marketingQueries = marketingDataSources.map(d => ({
        ...queryRequest,
        where: marketingWhere,
        mainDataSource: d,
        dataSources: [d],
        table: queryRequest?.table || 'architectureAndReport',
      }));
      queries.push(...marketingQueries);
      const dataSourceFieldInfoBySourceId = analyticDataSources.reduce((acc, dataSource) => {
        const dataSourceInfo = getAnalyticsInfo(dataSource);
        const dataSourceFields = getDataSourceFields(dataSource, queryRequest.fields, dataSourceInfo.dashboardPrefix);
        const { basicFields, computedFields } = dataSourceFields.reduce(
          (acc, field) => {
            if (field.startsWith(dataSourceInfo.dashboardPrefix)) {
              acc.basicFields.push(field);
            } else {
              acc.computedFields.push(field);
            }
            return acc;
          },
          { basicFields: [], computedFields: [] },
        );
        acc[dataSource.dataSourceId] = {
          basicFields,
          computedFields,
          rawFieldByField: this.getRawFieldByField({ fields: basicFields, dataSource }),
        };
        return acc;
      }, {});
      if (this.debug) {
        console.log('dataSourceFieldInfoBySourceId', dataSourceFieldInfoBySourceId);
      }
      const analyticQueries = analyticDataSources
        .map(analyticDataSource => {
          const analyticInfo = getAnalyticsInfo(analyticDataSource);
          const dataSourceFieldInfo = dataSourceFieldInfoBySourceId[analyticDataSource.dataSourceId];
          const fieldToCell = _.invert(dataSourceFieldInfo.rawFieldByField);
          const fields = dataSourceFieldInfo.computedFields.concat(
            dataSourceFieldInfo.basicFields.map(f => dataSourceFieldInfo.rawFieldByField[f]),
          );
          console.log(level);
          if (fields.length === 0 || !analyticInfo.marketingJoinFieldByLevel[level]) {
            return null;
          }
          const on = {};
          for (const joinField of analyticInfo.marketingJoinFieldByLevel[level]) {
            on[joinField] = analyticInfo.fieldMapping[joinField];
          }
          const parentQueryRequest = _.cloneDeep(queryRequest);
          const when = _.cloneDeep(queryRequest.when);
          const leftBreakdown = parentQueryRequest.breakdowns[0];
          const rightBreakdowns = Object.values(on);
          if (dateGroupBreakdowns.includes(leftBreakdown)) {
            if (leftBreakdown === 'autoTime') {
              rightBreakdowns.push(getAutoDateGroup({ from: queryRequest.when.from, to: queryRequest.when.to }));
            } else {
              rightBreakdowns.push(leftBreakdown);
            }
            parentQueryRequest.breakdowns = ['all'];
          } else if (parentQueryRequest.when.groupBy !== 'all') {
            if (parentQueryRequest.when.groupBy === 'autoTime') {
              when.groupBy = getAutoDateGroup({ from: queryRequest.when.from, to: queryRequest.when.to });
            } else {
              when.groupBy = parentQueryRequest.when.groupBy;
            }
            rightBreakdowns.push(when.groupBy);
            parentQueryRequest.when.groupBy = 'all';
          }
          if (!marketingDataSources || marketingDataSources.length === 0) {
            return null;
          }
          let joinedQueryWhere = null;
          if (analyticInfo.dataSourcePrefix === 'ga') {
            joinedQueryWhere = gaWhere;
          } else if (analyticInfo.dataSourcePrefix === 'ga4') {
            joinedQueryWhere = ga4Where;
          }
          if (!isGAAutoMatchingEnabled && ['googleAnalytics', 'googleAnalytics4'].includes(analyticDataSource.type)) {
            // it is not supported for other breakdowns
            const hasValidBreakdowns = ['all', 'day', 'week', 'month'].includes(queryRequest.breakdowns[0]);
            if (!withTotal && !hasValidBreakdowns) {
              return null;
            }
            let breakdown = 'all';
            if (hasValidBreakdowns) {
              breakdown = queryRequest.breakdowns[0];
            }

            const genericQuery = {
              ...parentQueryRequest,
              dataSources: [analyticDataSource],
              table: 'analytic',
              fields,
              aliases: fieldToCell,
              breakdowns: [breakdown],
              when,
              where: [],
              limit: undefined,
            };
            if (withTotal && !hasValidBreakdowns) {
              genericQuery.withTotal = true;
            }
            return genericQuery;
          } else {
            return {
              ...parentQueryRequest,
              where: marketingWhere,
              fields: [..._.intersection(queryRequest.fields, Object.values(CREATIVE_INSIGHT_FILED_BY_BREAKDOWN))],
              table: 'architectureAndReport',
              mainDataSource: analyticDataSource,
              aliases: fieldToCell,
              dataSources: marketingDataSources,
              joinedQueries: [
                {
                  query: {
                    ...parentQueryRequest,
                    dataSources: [analyticDataSource],
                    table: 'analytic',
                    fields,
                    aliases: fieldToCell,
                    breakdowns: rightBreakdowns,
                    when,
                    where: joinedQueryWhere,
                    limit: undefined,
                  },
                  on,
                },
              ],
            };
          }
        })
        .filter(Boolean);
      queries.push(...analyticQueries);
      const blendDataQueries = blendDataUtils.getBlendDataQueries({
        blendedDataSources: customDataSources.filter(d => d.settings),
        queryRequest: { ...queryRequest, where: marketingWhere },
      });
      if (this.debug) {
        console.log('blendDataQueries', blendDataQueries);
      }
      queries.push(...blendDataQueries);
      const customDataSourcesWithoutSettings = customDataSources.filter(d => !d.settings);
      const customDataSourcesWithoutSettingsQueries = customDataSourcesWithoutSettings.map(d => ({
        ...queryRequest,
        mainDataSource: d,
        dataSources: [d],
        table: 'custom',
      }));
      queries.push(...customDataSourcesWithoutSettingsQueries);
    }

    queries.forEach(query => (query.queryId = this.getQueryId(query)));
    return queries;
  }

  getOrInitUnionWorkerFromPool(index) {
    if (index > unionWorkerCount) {
      index = index % unionWorkerCount;
    }
    let worker = unionWorkerList[index];
    if (!worker) {
      // NOTE: The type is removed by the worker-plugin (added in the frontend project)! 'module' is not well supported by all browser (FF and Safari).
      // Therefore the plugin removes the options and the default is then type: 'classic'.
      // But if you remove the type or set it to 'classic' it will not work in the browser (tested on Linux/Fedora 32 and FireFox)
      // Source:
      // - https://www.npmjs.com/package/worker-plugin
      // - https://developer.mozilla.org/en-US/docs/Web/API/Worker/Worker#browser_compatibility
      // @ts-ignore
      worker = new Worker('./WorkerUnionTables.js', { type: 'module' });
      unionWorkerList[index] = worker;
    }
    return worker;
  }

  /**
   * Async function that uses a pool of Web Worker to process mergeDashboardTableRowsByDimension()
   * @param {object} p
   * @param {DashboardTableRow[]} p.tables
   * @param {Object[]} [p.customMetrics]
   */
  async workerUnionTables({ tables, customMetrics, originalDateFilter = undefined }) {
    if (!this.useWorker) {
      return unionTables({
        tables,
        customMetrics,
        originalDateFilter,
      });
    }

    unionWorkerIndex = (unionWorkerIndex + 1) % unionWorkerCount;
    const inUseWorkerIndex = unionWorkerIndex;
    const worker = this.getOrInitUnionWorkerFromPool(inUseWorkerIndex);
    const uuid = v4();

    return new Promise((resolve, reject) => {
      const callback = e => {
        if (e.data.key === uuid) {
          worker.removeEventListener('message', callback);
          let result = e.data.result;
          // sometimes I got an empty array as a result, I didn't manage to reproduce easily
          if (!result || (Array.isArray(result) && result.length === 0)) {
            result = unionTables({ tables, customMetrics, originalDateFilter });
          }
          resolve(result);
        }
      };

      worker.postMessage({
        tables,
        customMetrics,
        key: uuid,
      });
      worker.addEventListener('message', callback);
      worker.onerror = err => {
        worker.terminate();
        unionWorkerList[inUseWorkerIndex] = undefined;
        reject(err);
      };
    });
  }

  async getQueryTable({
    queries,
    workspaceId,
    currency,
    customMetrics,
    reRenderCallBack = null,
    failCallBack = null,
    doneCallBack = null,
    parentRow = null,
    view = null,
    originalDateFilter = undefined,
  }) {
    if (this.debug) {
      // @ts-ignore
      console.log(`getQueryTable Input`, arguments[0]);
    }

    // Get cached tables
    let cachedTables = [];
    if (!this.noCache) cachedTables = await this.query({ workspaceId, currency, queries, useCache: true, view });
    cachedTables.forEach(t => {
      t.isExpired = this.noCache || moment().diff(t.refreshTime, 'hours') > 1;
      if (parentRow) {
        t.rows.forEach(row => {
          row.id = `${parentRow.id}-${row.id}`;
        });
      }
    });
    const hasAllQueryTables = cachedTables.length === queries.length;
    const jobsInfos = cachedTables.map(cts => cts.refreshWidgetJobInfo);
    const cachedTable = await this.workerUnionTables({
      tables: cachedTables,
      customMetrics,
      originalDateFilter,
    });
    const refreshTime = _.min(cachedTables.map(t => t.refreshTime));
    let isCacheLoaded = false;
    if (hasAllQueryTables && !this.noCache) {
      isCacheLoaded = true;
      reRenderCallBack({ table: cachedTable, refreshTime, isCacheLoaded });
      if (cachedTables.every(t => !t.isExpired)) {
        // the cache is less than 1 hour we can stop here
        doneCallBack({ table: cachedTable, refreshTime, isCacheLoaded, debug: this.debug, jobsInfos });
        if (this.debug) {
          console.log(`getQueryTable Output`, { queries, table: cachedTable });
        }
        // check with Olivier : can it create a problem if I return jobsInfos there ?
        return jobsInfos;
      }
    } else {
      reRenderCallBack({ table: cachedTable, refreshTime, isCacheLoaded }); // FE tables doesn't seem to work if we use only the doneCallBack in the next block
    }
    if (this.onlyCache) {
      // Avoid no response from server. Possibly....
      if (typeof window === 'undefined') {
        doneCallBack({ table: cachedTable, refreshTime, isCacheLoaded, debug: this.debug, jobsInfos });
      }
      return jobsInfos;
    }

    const unexpiredCachedTable = await this.workerUnionTables({
      tables: cachedTables.filter(t => !t.isExpired),
      customMetrics,
    });
    const cachedTablesByQueryId = _.keyBy(cachedTables, t => t.queryId);
    const tableByQueryId = queries.reduce((acc, query) => {
      if (acc[query.queryId]) {
        console.error('dashboardClient - duplicate query id', queries);
      }
      const cachedTable = cachedTablesByQueryId[query.queryId];
      let queryInfo = {};
      if (cachedTable) {
        queryInfo = cachedTable;
      }
      acc[query.queryId] = queryInfo;
      return acc;
    }, {});
    let widgetId = Math.round(Math.random() * 1000000000);
    // refresh tables or fetch the missing tables
    let table: any = unexpiredCachedTable;
    await Bluebird.map(queries, async query => {
      const dataSource = query.mainDataSource;
      let queryTable = tableByQueryId[query.queryId];
      if (!queryTable.hasOwnProperty('rows') || queryTable.isExpired) {
        try {
          queryTable = (await this.query({ workspaceId, currency, queries: [query], useCache: false, view })) || {};
          if (parentRow) {
            queryTable.rows.forEach(row => {
              row.id = `${parentRow.id}-${row.id}`;
            });
          }
          const refreshTime = new Date().toISOString();
          queryTable.refreshTime = refreshTime;
          queryTable.isExpired = false;
          tableByQueryId[query.queryId] = queryTable;
          // Without the lock, the table can be erased in the middle of the process by another query.
          let nav = null;
          if (typeof navigator == 'undefined') {
            console.error(
              '/!Strong warning/! There can be a race condition if this function is run outside of the browser.',
            );
            nav = { locks: { request: async (name, fun) => await fun() } };
          } else {
            nav = navigator;
          }
          await nav.locks.request(`aggregateTable_${widgetId}`, async () => {
            table = await this.workerUnionTables({
              tables: [queryTable, table],
              customMetrics,
            });
            const expiredTables = [];
            for (const table of Object.values(tableByQueryId)) {
              // @ts-ignore
              if (table.hasOwnProperty('rows') && table.isExpired) {
                expiredTables.push(table);
              }
            }
            const tableToReRender = await this.workerUnionTables({
              tables: [...expiredTables, table],
              customMetrics,
            });
            reRenderCallBack({ table: tableToReRender, refreshTime, dataSource: query.mainDataSource, isCacheLoaded });
          });
        } catch (error) {
          console.error('queryTable', error);
          if (!queryTable.rows && failCallBack) {
            const isArchitectureSynced = Boolean(dataSource.architectureSyncedAt);
            if (!isArchitectureSynced) {
              error = { code: 'ARCHITECTURE_NOT_SYNCED', message: 'architecture is not synced' };
              console.error('ARCHITECTURE_NOT_SYNCED', { dataSource, error });
            }
            queryTable.error = error;
            failCallBack({ dataSource, error });
          } else {
            console.error('Error while refreshing: channel', dataSource.connectorName);
          }
        }
        tableByQueryId[query.queryId] = queryTable;
      }
    });
    const doneRefreshTime = _.min(
      Object.values(tableByQueryId)
        // @ts-ignore
        .flatMap(queryTable => queryTable.refreshTime)
        .filter(Boolean),
    );
    // const hasError = Object.values(tableByQueryId).some(queryTable => queryTable.error);
    doneCallBack({ table, refreshTime: doneRefreshTime, isCacheLoaded, debug: this.debug });
    if (this.debug) {
      console.log(`getQueryTable Output`, { queries, table });
    }
    return table;
  }

  getDataSources({ adAccounts, accountSets, dashboardFilter, dimensionId, isCustomTable, metrics, widget }) {
    ({ adAccounts, dashboardFilter } = filterAdAccounts({ adAccounts, dashboardFilter, dimensionId }));
    if (adAccounts.length === 0) {
      return [];
    }
    if (!accountSets?.length) {
      accountSets = this.getAccountSets({
        adAccounts,
        useAllAdAccounts: !dashboardFilter.adAccountIds && !dashboardFilter.channels,
        isCustomTable,
        metrics,
        noWidgetPassed: !widget,
        dimensionId,
      });
    }
    const dataSources = accountSets.map(({ adAccount }) => convertFormat.getAdAccountToDataSource(adAccount));
    if (this.debug) {
      console.log('getDataSources', dataSources);
    }
    return dataSources;
  }

  async getAvailableFields({
    adAccounts,
    isCustomTable = false,
    currentBreakdowns = [],
    useObjectFormat = false,
    groupByDataSource = false,
  }) {
    this;
    const dataSources = adAccounts.map(adAccount => convertFormat.getAdAccountToDataSource(adAccount));
    const dataType = isCustomTable ? 'normal' : 'marketing';
    const response = await this.axios.post(
      'dashboard/dataSource/availableFields',
      {
        dataSources,
        dataType,
        useObjectFormat,
        ...(currentBreakdowns.length > 0 && { currentBreakdowns }),
        groupByDataSource,
      },
      { params: { c: dataType } },
    );
    return response.data;
  }

  /**
   * Get the data from the overview graph.
   * @param {object} p
   * @param {ConnectAdAccount[]} p.adAccounts ad accounts.
   * @param {DashboardFilter} p.dashboardFilter
   * @param {DateFilter} p.dateFilter
   * @param {function} [p.reRenderCallBack]
   * @param {function} [p.failCallBack]
   * @param {function} [p.doneCallBack]
   * @returns {Promise<{table: DashboardTableRow, adAccountTable: DashboardTable, isSuccess: Boolean}>[]}
   */
  getOverviewGraphs({ dashboardFilter }) {
    return this.getTable({ ...arguments[0], dashboardFilter, dimensionId: 'autoTime', includeTotal: true });
  }

  getGraph({
    adAccounts,
    dateFilter,
    dashboardFilter,
    metricFilter,
    dimensionId,
    customMetrics = [],
    widgetId = null,
    orderBy,
    orderDirection,
    reRenderCallBack = undefined,
    failCallBack = undefined,
    doneCallBack = undefined,
    widget = null,
    childrenDimensionId = null,
    isCustomTable = false,
  }) {
    if (this.debug) {
      console.log('getGraph-input', arguments[0]);
    }
    return this.getTable({
      ...arguments[0],
      others: { includeOthers: true, limit: 10 },
      ...(!isCustomTable && { sorting: { field: 'spend', direction: 'desc' } }),
    });
  }

  async getWidgetTable(
    widget: DashboardWidget,
    view: DashboardView,
    timeZone,
    highestParentFolder: DashboardFolder = null,
    reRenderCallBack = null,
    failCallBack = null,
    doneCallBack = null,
    useComparisonDate: boolean = false,
    dimensions: any[],
    dashboardFilter?: Record<string, any>,
    dateFromFE = null, // used only for testing
    onlyArgs = false, // used only for testing
  ) {
    if (view == null) {
      return {};
    }

    // Panel Widgets sends in the dashboardFilter as a param, then do not modify the dashboardFilter object
    let doNotModifyDashboardFilter = !!dashboardFilter;
    // parentFolderFilter is should be put into the dashboard filter
    // const parentFilterFilters = [];
    // for (const folder of parentFolders) {
    //   if (folder?.config?.filterTagList) {
    //     parentFilterFilters.push(folder.config.filterTagList);
    //   }
    //   if ('filterTagList' in folder) {
    //     parentFilterFilters.push(folder.filterTagList);
    //   }
    // }
    // const parentFolerDashboardFilters = parentFilterFilters.map(convertDashboardFilterTagListToParsedList);
    const existingDashboardFilterArray = dashboardFilter
      ? Object.entries(dashboardFilter).map(([key, value]) => ({ [key]: value }))
      : [];
    const dashboardFilterArray = [...existingDashboardFilterArray];
    const highestParentFilterFilter = highestParentFolder?.config?.filterTagList ?? highestParentFolder?.filterTagList;
    if (highestParentFilterFilter) {
      const highestParentFolderDashboardFilter = convertDashboardFilterTagListToParsedList(highestParentFilterFilter);
      dashboardFilterArray.push(highestParentFolderDashboardFilter);
    }
    dashboardFilter = mergeFiltersToParamsWithoutClear(dashboardFilterArray);

    // dashboardFilter
    if (!doNotModifyDashboardFilter) {
      let filterTagsLists: DashboardFEFilterTagList[] = [];
      if ('filterTagList' in widget) filterTagsLists.push(widget.filterTagList);
      if (view.config.filterTagList) filterTagsLists.push(view.config.filterTagList);
      const dashboardFilters = filterTagsLists.map(convertDashboardFilterTagListToParsedList);
      if (highestParentFilterFilter) {
        const highestParentFolderDashboardFilter = convertDashboardFilterTagListToParsedList(highestParentFilterFilter);
        dashboardFilters.push(highestParentFolderDashboardFilter);
      }
      dashboardFilter = mergeFiltersToParams(dashboardFilters);

      if (widget.type == 'pivotTable' && widget.subType == 'analytic') {
        dashboardFilter = {};
      }
    }

    let { dateFilter, originalDateFilter, comparisonDateFilter } = getParsedDateFilter(widget, view, timeZone);

    if (dateFromFE) {
      if (dateFilter.from == dateFromFE.from && dateFilter.to == dateFromFE.to) {
        useComparisonDate = false;
      } else if (comparisonDateFilter.from == dateFromFE.from && comparisonDateFilter.to == dateFromFE.to) {
        useComparisonDate = true;
      } else {
        throw new Error(
          JSON.stringify({ message: 'getWidgetTable: bad date.', dateFilter, comparisonDateFilter, dateFromFE }),
        );
      }
    }
    if (useComparisonDate) dateFilter = comparisonDateFilter;
    if (moment(dateFilter.from).isAfter(dateFilter.to)) {
      const emptyResult = { table: [] };
      doneCallBack(emptyResult);
      return emptyResult;
    }
    // ad accounts
    const allAdAccounts: ObsoleteAdAccount[] = await this.memoizedGetAdAccounts();
    const adAccounts = selectDataSources(allAdAccounts, widget);
    // custom metrics
    const customMetrics = await this.memoizedGetCustomMetrics(view.id);
    // callbacks
    const callbacks = {
      reRenderCallBack,
      failCallBack,
      doneCallBack,
    };
    // The widgets are not always ordered in the same order for view.config.widgets and view.config.viewLayout.layout. Most of the time it is OK, but no always
    // const widgetInd = view.config.widgets.findIndex(w => w.id == widget.id);
    const widgetLayout = view?.config?.viewLayout.layout.find(l => l.i === widget.id);

    // isCustomTable (TODO: change the logic). Do as BK does now, then change later
    // This is BK logic
    let isCustomTable =
      widget.type === 'pivotTable'
        ? widget.type === 'pivotTable' && widget.subType === 'analytic' // if widget.type == "pivotTable" then special case
        : checkSourceTableMode(adAccounts) === 'analytic'; // For every widget except "pivotTable" according to BK
    // dimension id
    let dimensionId = null;
    let childrenDimensionId = null;
    let others = null;
    let sorting = null;
    let includeTotal = false;
    if (widget.type == 'pivotTable') {
      dimensionId = widget.dimensionIds[0] || '';
      includeTotal = true;
    } else if (widget.type == 'creative') {
      dimensionId = R.pathOr('ad', ['dimensionIds', 0], widget);
      includeTotal = true; // why?
    } else if (widget.type == 'singleMetric') {
      dimensionId = 'all';
    } else if (widget.type === WIDGET_TYPE.goal) {
      dimensionId = 'all';
    } else if (widget.type == 'smallTable') {
      const SMALL_TABLE_FIXED_HEIGHT_SIZE = 6;
      // const availableRows = (widgetLayout?.h || 0) - SMALL_TABLE_FIXED_HEIGHT_SIZE;
      // const numOfRows = Math.max(1, availableRows);
      dimensionId = widget.dimensionIds[0]?.toString() || '';
      // others = { includeOthers: true, limit: numOfRows };
      const isCustomBreakdown = parseInt(dimensionId) == dimensionId;
      const sortBy = R.pathOr(false, ['sort', 'name'], widget);
      if (sortBy) {
        const isDesc = R.pathOr(true, ['sort', 'isDesc'], widget);
        sorting = {
          field: R.pathOr('', ['columns', 0], widget),
          direction: isDesc ? 'desc' : 'asc',
        };
        // sorting = { field: widget.sort.name, direction: widget.sort.isDesc ? "desc" : "asc" }
        // if (sorting.field == "metric")
        //   sorting.field = widget.columns[0]
      }
      includeTotal = !isCustomBreakdown;
    } else if (widget.type == 'graph') {
      let graphType = widget?.selectedGraphType;

      // // Special case for Graphs for adAccounts and isCustomTable.
      // const hasAdAccountsKey = R.has('adAccounts', widget);
      // if (!hasAdAccountsKey) {
      //   const adAccountsForGraph = R.filter(isGraphSource,allAdAccounts)
      //   adAccounts = adAccountsForGraph;
      // }

      // See getGraph() -- getGraph() is only called if !CHART_TYPES_FOR_GET_TABLE.includes(graphType) === true
      if (!CHART_TYPES_FOR_GET_TABLE.includes(graphType)) {
        others = { includeOthers: true, limit: 10 };
        if (!isCustomTable) {
          const field = widget.selectedMetric ?? 'spend';
          sorting = { field, direction: 'desc' };
        }
      }

      if (graphType === CHART_TYPES.DONUT) {
        includeTotal = true;
      }

      // if (!isCustomTable) {
      //   if (!CHART_TYPES_FOR_GET_TABLE.includes(graphType)) {
      //     sorting = { field: "spend", direction: "desc" }
      //     others = { includeOthers: true, limit: 10 }
      //   }
      // }

      // isCustomTable =
      // TODO: fix dimensionId
      // see file: src/components/Dashboard/GraphWidget/index.js function: selectedDimensionId()
      const DEFAULT_BREAKDOWN = 'channel'; // found in src/components/Dashboard/GraphWidget/helper.js
      // dimensionId = widget.dimensionIds[0]?.toString() || DEFAULT_BREAKDOWN; // || DEFAULT_BREAKDOWN found in src/components/Dashboard/GraphWidget/index.js
      // const dimensions = await this.getCustomBreakdowns();
      dimensionId = selectedDimensionId(widget, graphType, dimensions, useComparisonDate);
      if (graphType === 'bar' || graphType == 'line' || graphType == 'horizontal_bar') {
        const isNoneDimensionId = widget.dateBreakdown === 'none';
        const dateBreakdown = (childrenDimensionId = widget.dateBreakdown || 'autoTime');
        childrenDimensionId = isNoneDimensionId ? null : dateBreakdown;

        // dateFilter.dateGroup = dateGroup || "autoTime"
        // others = { includeOthers: true, limit: 10 }
        // sorting = { field: "spend", direction: "desc" }

        if (isCustomTable) {
          sorting = undefined;
        }
      } else if (graphType === 'pie' || graphType === 'scatter') {
        // others = { includeOthers: true, limit: 10 } // FE calculates "other" for now
        // sorting = { field: "spend", direction: "desc" }
      } else {
        // dateFilter.dateGroup = dateGroup || "autoTime"
      }

      // This should be covered by selectedDimensionId()
      // if (graphType == 'wordCloud') {
      //   dimensionId = "creativeWord"
      // }
      // if (graphType == 'color_clustering') {
      //   dimensionId = "colorClustering"
      // }
      // if (graphType == 'emoji_analysis') {
      //   dimensionId = "emojiAnalysis"
      // }
      // if (useComparisonDate || graphType == "funnel") {
      //   dimensionId = "all"
      // }
    } else if (widget.type == 'graphBoxes') {
      dimensionId = 'autoTime';
      includeTotal = true;
    } else if (widget.type == 'alarms' || widget.type == 'notepad') {
      return null;
    }
    // change if custom table
    if (isCustomTable && !doNotModifyDashboardFilter) {
      dashboardFilter = {};
    }
    return new Promise(async (resolve, reject) => {
      try {
        // TODO: remove metric filters from graphs
        // children dimension id
        // acount sets??
        let args = {
          // resolve(({
          dateFilter,
          adAccounts,
          dashboardFilter,
          // widgetId: widget.id + (useComparisonDate ? (widget.type == "smallTable" ? "-comparison" : "_comparison") : ""),
          widgetId: widget.id + (useComparisonDate ? (widget.type == 'graph' ? '_comparison' : '-comparison') : ''),
          dimensionId,
          childrenDimensionId,
          metricFilter: null, // seems not used, TODO: check
          reRenderCallBack: function () {
            reRenderCallBack(...arguments);
          },
          failCallBack: function () {
            failCallBack(...arguments);
            reject(arguments[0]);
          },
          doneCallBack: function () {
            doneCallBack(...arguments);
            resolve(arguments[0]);
          },
          others,
          accountSets: null, // Cannot find any mention of this in the FE code.
          forceRefreshCache: this.noCache,
          customMetrics,
          includeTotal,
          isCustomTable,
          widget,
          limit: null,
          sorting,
          view,
          originalDateFilter,
        };
        if (onlyArgs) {
          resolve(args);
          return;
        }

        const result = await this.legacyGetTable(args);
        resolve(result);
      } catch (error) {
        reject(error);
      }
    });
  }

  async debugGetTable(argument) {
    if (this.debug) {
      // Send events if in a browser context. Used for debugging with Puppeteer
      if (typeof document === 'object') {
        sendBrowserEventsToForGetTableFunction(argument);
      }
      const timeZone =
        typeof Intl?.DateTimeFormat === 'undefined' ? 'Asia/Seoul' : Intl.DateTimeFormat().resolvedOptions().timeZone;
      this;
      const dimensions = await this.getCustomBreakdowns();
      let widgetTableArgs = await this.getWidgetTable(
        argument.widget,
        argument.view,
        timeZone,
        argument.folders,
        NOOP,
        NOOP,
        NOOP,
        argument.isComparison,
        dimensions,
        argument.dashboardFilter,
        null,
        true,
      );

      // Start with default arguments, same as used in legacyGetTable() function
      const copyArgument = {
        customMetrics: [],
        isCustomTable: false,
        forceRefreshCache: false,
        includeTotal: false,
        // ...argument
      };
      // Copy the input arguments into the new copyArgument object
      Object.keys(argument).forEach(key => {
        copyArgument[key] = argument[key];
      });

      notUsedKeys.map(key => {
        delete copyArgument[key];
        delete widgetTableArgs[key];
      });

      let problems = compareJSON(copyArgument, widgetTableArgs);
      console.log('#WT0 problems:', { problems: problems, argument, widgetTableArgs });
      // Send events if in a browser context, in Nodejs context just log on problems found
      if (typeof document === 'object') {
        if (problems != undefined && Object.keys(problems).length > 0) {
          const allAdAccounts = await this.memoizedGetAdAccounts();
          let eventCustom = new CustomEvent('PUPPETEER_CUSTOM');
          // @ts-ignore
          eventCustom.pup_prop = {
            args: {
              widgetType: argument?.widget?.type,
              widgetId: argument?.widget?.id,
              problems,
              getTableArgs: copyArgument,
              widgetTableArgs,
              allAdAccounts,
            },
            type: `${argument?.widget?.id ? argument?.widget?.id : ''}_problems`,
          };
          document.dispatchEvent(eventCustom);
          //@ts-ignore
          console.log('#WT1', eventCustom.pup_prop);
        } else {
          let eventCustom = new CustomEvent('PUPPETEER_CUSTOM');
          // @ts-ignore
          eventCustom.pup_prop = {
            args: {
              widgetId: argument?.widget?.id,
              getTableArgs: copyArgument,
              widgetTableArgs,
            },
            type: `${argument?.widget?.id ? argument?.widget?.id : ''}_good`,
          };
          document.dispatchEvent(eventCustom);
        }
      } else {
        if (problems != undefined && Object.keys(problems).length > 0) {
          console.log('GetTable problems found:', {
            widgetType: argument?.widget?.type,
            widgetId: argument?.widget?.id,
            problems,
            getTableArgs: copyArgument,
            widgetTableArgs,
          });
        }
      }
    }
  }

  // old version for quick roll-back
  // async getTable(argument) {
  //   this.debugGetTable(argument);

  //   const widget = argument?.widget;
  //   const view = argument?.view;
  //   let _aborted = argument.aborted ?? { value: false };

  //   // If no widget or widgetId, then call legacyGetTable() as a safeguard
  //   if (!this.useWidgetTable || !(widget || argument?.widgetId)) {
  //     // console warning, check if we can log event in APM or something
  //     // call legacy table
  //     // console.warn('DEPRECATED! Missing widget, so calling legacyGetTable()', argument);
  //     return await this.legacyGetTable(argument);
  //   }

  //   const baseRoute = 'dashboard/widgetTable';
  //   this; // <-- I have no idea why, but removing this line leads to an error "typeError: this.retryAxios is undefined."
  //   /**
  //    * Removing the view.config.widgets, since it can get very big and adds a lot of "Sending" time. If view is big, it can take several seconds.
  //    * BE does not use view.config.widgets, we already send the widget to BE in the body!
  //    */
  //   const lightView = lightenView(view);

  //   /**
  //    * if the browser doesn't support Intl.DateTimeFormat, we just use the Seoul timezone
  //    * we can add a polyfill to improve this
  //    */
  //   const timeZone = getTimezone();
  //   const forceRefresh = argument.retryNoCache ?? this.noCache;
  //   const dimensionId = widget?.dimensionIds?.[0] ?? null;

  //   try {
  //     const result = await this.retryAxios.post(
  //       `${baseRoute}/?type=${widget?.type}&breakdown=${dimensionId}&forceRefresh=${forceRefresh}&retryCount=${
  //         argument.retryCount || 0
  //       }`,
  //       {
  //         workspaceId: this.accountId,
  //         widgetId: widget.id,
  //         viewId: lightView.id,
  //         lastRefreshedTime: argument.lastRefreshedTime,
  //         jobIds: argument.jobIds,
  //         //for now send the will widget & view cause FE does not auto save
  //         forceRefresh, // do not send forceRefresh when re-sending the request. Only use forceRefresh for the first request
  //         widget,
  //         view: lightView,
  //         useComparisonDate: argument.isComparison,
  //         dashboardFilter: argument.dashboardFilter,
  //         timeZone,
  //       },
  //       RETRY_AXIOS_OPTION,
  //     );

  //     const data = result.data;
  //     const jobs = data.jobsInfos ?? [];

  //     handleGetTableFailsIfAny({ jobs, callback: argument.failCallBack });

  //     // jobs can be undefined when the widget has an AdAccount that is deleted(?) but still attached to the widget
  //     const someJobsAreNotFinished = Array.isArray(jobs) && jobs.some(job => job?.status === 'IN_PROCESS');

  //     if (!someJobsAreNotFinished) {
  //       // stop calling getWidget
  //       argument.doneCallBack(data);
  //       return;
  //     } else {
  //       // call again getWidget to have fresher data
  //       if (!data.nothingToRender) {
  //         argument.reRenderCallBack(data);
  //       }

  //       const lastRefreshedTime = Date.now();
  //       const retryCount = argument.retryCount || 1;

  //       // recursive call
  //       setTimeout(async () => {
  //         // DO NOT recursive call when aborted
  //         if (_aborted.value) {
  //           return;
  //         }
  //         await this.getTable({
  //           ...argument,
  //           retryNoCache: false,
  //           lastRefreshedTime,
  //           jobIds: jobs.map(j => j?.jobId),
  //           retryCount: retryCount + 1,
  //           aborted: _aborted,
  //         });
  //       }, Math.min(500 * retryCount, 2000));
  //     }
  //   } catch (err) {
  //     argument.failCallBack({ error: err });
  //     return;
  //   }

  //   // return abort function to save calls
  //   return () => {
  //     _aborted.value = true;
  //   };
  // }

  async getTable(argument) {
    let _abortController = argument.abortController ?? new AbortController();

    this.debugGetTable(argument);

    const widget = argument?.widget;
    const view = argument?.view;

    // If no widget or widgetId, then call legacyGetTable() as a safeguard
    if (!this.useWidgetTable || !(widget || argument?.widgetId)) {
      // console warning, check if we can log event in APM or something
      // call legacy table
      // console.warn('DEPRECATED! Missing widget, so calling legacyGetTable()', argument);
      return await this.legacyGetTable(argument);
    }

    const baseRoute = 'dashboard/widgetTable';
    this; // <-- I have no idea why, but removing this line leads to an error "typeError: this.retryAxios is undefined."
    /**
     * Removing the view.config.widgets, since it can get very big and adds a lot of "Sending" time. If view is big, it can take several seconds.
     * BE does not use view.config.widgets, we already send the widget to BE in the body!
     */
    const lightView = lightenView(view);

    /**
     * if the browser doesn't support Intl.DateTimeFormat, we just use the Seoul timezone
     * we can add a polyfill to improve this
     */
    const timeZone = getTimezone();
    const forceRefresh = argument.retryNoCache ?? this.noCache;
    const dimensionId = widget?.dimensionIds?.[0] ?? null;

    this.retryAxios
      .post(
        `${baseRoute}/?type=${widget?.type}&breakdown=${dimensionId}&forceRefresh=${forceRefresh}&retryCount=${
          argument.retryCount || 0
        }&widgetName=${encodeURIComponent((widget?.widget_title || '').replace(/\s/g, '_'))}`,
        {
          workspaceId: this.accountId,
          widgetId: widget.id,
          viewId: lightView.id,
          lastRefreshedTime: argument.lastRefreshedTime,
          jobIds: argument.jobIds,
          //for now send the will widget & view cause FE does not auto save
          forceRefresh, // do not send forceRefresh when re-sending the request. Only use forceRefresh for the first request
          widget,
          view: lightView,
          useComparisonDate: argument.isComparison,
          // dashboardFilter: argument.dashboardFilter,
          timeZone,
        },
        {
          ...RETRY_AXIOS_OPTION,
          signal: _abortController.signal,
        },
      )
      .then(result => {
        const data = result.data;

        // Case 1: Pure server error (no job data, just error code/message)
        // e.g. { code: "INTERNAL_ERROR", message: "..." }
        if (result.status >= 400 && !data?.jobsInfos) {
          console.error('[getTable] Server error without job data:', data);
          argument.failCallBack({ error: data });
          // to end load. if not, it will keep loading.
          argument.doneCallBack(data);
          return;
        }

        // Case 2: Job-level failures (response has jobsInfos with individual job statuses)
        const jobs = data.jobsInfos ?? [];
        handleGetTableFailsIfAny({ jobs, callback: argument.failCallBack });

        // jobs can be undefined when the widget has an AdAccount that is deleted(?) but still attached to the widget
        const someJobsAreNotFinished = Array.isArray(jobs) && jobs.some(job => job?.status === 'IN_PROCESS');

        if (!someJobsAreNotFinished) {
          // stop calling getWidget
          argument.doneCallBack(data);
          return;
        } else {
          // call again getWidget to have fresher data
          if (!data.nothingToRender) {
            argument.reRenderCallBack(data);
          }

          const lastRefreshedTime = Date.now();
          const retryCount = argument.retryCount || 0;

          // recursive call
          const ms = _getMsForRetry(retryCount);

          setTimeout(async () => {
            // DO NOT recursive call when aborted

            await this.getTable({
              ...argument,
              view: lightView,
              retryNoCache: false,
              lastRefreshedTime,
              jobIds: jobs.map(j => j?.jobId),
              retryCount: retryCount + 1,
              abortController: _abortController,
            });
          }, ms);
        }
      })
      .catch(err => {
        if (isRequestCancelled(err)) return;
        argument.failCallBack({ error: err });
      });

    // return abort function to save calls
    return () => {
      _abortController.abort();
    };
  }

  async legacyGetTable({
    adAccounts = null,
    dateFilter,
    dashboardFilter,
    metricFilter,
    dimensionId,
    childrenDimensionId,
    widgetId = null,
    reRenderCallBack = null,
    failCallBack = null,
    doneCallBack = null,
    includeTotal = false,
    others = null, // { includeOthers: true, limit: 5 },
    // doCleanDashboardFilter = true,
    accountSets = null,
    forceRefreshCache = false,
    customMetrics = [],
    isCustomTable = false,
    widget = null,
    limit = null,
    sorting = null,
    view = null,
    originalDateFilter = null,
  }) {
    if (widget?.selectedGraphType === 'pie') {
      childrenDimensionId = null;
      limit = 50;
    }
    if (widget?.selectedGraphType === 'scatter') {
      childrenDimensionId = null;
      limit = 100;
    }

    const hasCustomBreakdown = typeof dimensionId === 'number';
    if (hasCustomBreakdown) {
      includeTotal = false;
      // others = { includeOthers: true, limit: null }; // if we want to include the total, we have to show others
    }

    const currency = this.currency;
    const callbacks = {
      reRenderCallBack,
      failCallBack,
      doneCallBack,
    };
    if (this.debug) {
      console.log(
        `getTable Input ${
          widget?.widget_title ? widget.widget_title : widget?.id
        }-${dimensionId}-${childrenDimensionId}`,
        // @ts-ignore
        arguments[0],
      );
    }
    _addRelativeDateRangeInfoIfLegacy(dateFilter);

    // Group ad accounts in sets. This is useful for GA.
    /**
     * TODO: replace "customMetrics: this.customMetrics" with just "customMetrics". customMetrics is already a param to the function!
     * Be careful about FE side. Could be needed there.
     * At least use it BE side!!!
     *
     * Do an IF check: if(isBrowser) { keep as is } else { getMetricsFromWidget({ widget, customMetrics }) }
     *
     * NOTE: Other functions where FE side get data from an BE endpoint through the Dashboar(Query)Client has this logic that sets the this.<paramName>.
     * When used on BE side, check how the logic is for these data points as well, such as getAdAccounts() possibly more!
     */
    const { metrics, rawMetrics } = getMetricsFromWidget({ widget, customMetrics: this.customMetrics });

    // ? Because CustomBreakdown request and CustomMetric is not possible to calculate CustomMetric data properly
    // ? https://app.asana.com/0/1203106898754068/1204775628471154/f
    if (others?.includeOthers === true) {
      const isCustomBreakdown = typeof dimensionId === 'number';
      const isCustomMetricRequested = rawMetrics.some((metric: string) => metric.startsWith('custom-metric:'));
      const shouldNotGetOthers = isCustomBreakdown || isCustomMetricRequested;
      if (shouldNotGetOthers) {
        others.skipToCalculateOthers = true;
      }
    }

    // Filter ad accounts to avoid useless calls.
    const dataSources = this.getDataSources({
      adAccounts,
      accountSets,
      dashboardFilter,
      dimensionId,
      isCustomTable,
      metrics,
      widget,
    });
    if (this.debug) {
      console.log('dataSources', dataSources);
    }
    if (dataSources.length === 0) {
      if (doneCallBack) {
        doneCallBack({ table: [], noDataSources: true });
        return [];
      }
    }

    // if (doCleanDashboardFilter) dashboardFilter = getCleanedDashboardFilter(dashboardFilter);

    const workspaceId = this.accountId;
    const queryRequest = convertFormat.getTableArgumentsToDataSourceQuery({
      workspaceId,
      currency,
      dimensionId,
      childrenDimensionId,
      dashboardFilter,
      dateFilter,
      metrics,
      limit,
      isCustomTable,
      widget,
    });
    const withTotal = includeTotal || others?.includeOthers;
    const queries = this.getQueries({
      queryRequest,
      dataSources,
      isCustomTable,
      isGAAutoMatchingEnabled: widget.isGAAutoMatchingEnabled ?? true,
      withTotal,
    });
    if (this.debug) {
      console.log('queries', queries);
    }
    let queryTable = null;
    if (includeTotal || others?.includeOthers) {
      // Include the total/sum and remaining of the table in the result, if requested.
      // support custom table now
      queryTable = await this.getTableWithTotal({
        queries,
        workspaceId,
        currency,
        customMetrics,
        sorting,
        others,
        includeTotal,
        reRenderCallBack: queryReRenderCallBack.bind(callbacks),
        failCallBack: queryFailCallBack.bind(callbacks),
        doneCallBack: queryDoneCallBack.bind(callbacks),
        view,
        originalDateFilter,
      });
    } else {
      queryTable = await this.getQueryTable({
        queries,
        workspaceId,
        currency,
        customMetrics,
        reRenderCallBack: queryReRenderCallBack.bind(callbacks),
        failCallBack: queryFailCallBack.bind(callbacks),
        doneCallBack: queryDoneCallBack.bind(callbacks),
        view,
        originalDateFilter,
      });
    }
    const convertedRows = convertFormat.getQueryRowsToTableRows(queryTable);

    if (
      this.debug &&
      (isCustomTable || widget?.selectedGraphType === 'pie' || widget?.selectedGraphType === 'scatter')
    ) {
      console.log(
        `getTableWithoutTotal
      ${widget?.widget_title ? widget?.widget_title : widget?.id}:${dimensionId}`,
        convertedRows,
      );
    }
    return { table: convertedRows };
  }

  async getChildrenOfTableRow(argument) {
    if (!this.useWidgetTable) {
      return await this.legacyGetChildrenOfTableRow(argument);
    }
    const baseRoute = 'dashboard/widgetTable/tablechildren';
    const forceRefresh = argument.retryNoCache ?? this.noCache;
    const dimensionId = argument.dimensionId;
    this; // <-- I have no idea why, but removing this line leads to an error "typeError: this.retryAxios is undefined."
    const timeZone =
      typeof Intl?.DateTimeFormat === 'undefined' ? 'Asia/Seoul' : Intl.DateTimeFormat().resolvedOptions().timeZone;

    const lightView = lightenView(argument.view);

    try {
      const result = await this.retryAxios.post(
        `${baseRoute}/?type=${argument.widget?.type}&breakdown=${dimensionId}&forceRefresh=${forceRefresh}&retryCount=${
          argument.retryCount || 0
        }&widgetName=${encodeURIComponent((argument.widget?.widget_title || '').replace(/\s/g, '_'))}`,
        {
          workspaceId: this.accountId,
          parentRow: argument.parentRow,
          dimensionId,
          // customMetrics: argument.customMetrics,
          lastRefreshedTime: argument.lastRefreshedTime,
          timeZone,
          widget: argument.widget,
          view: lightView,
          jobIds: argument.jobIds,
          forceRefresh, // do not send forceRefresh when re-sending the request. Only use forceRefresh for the first request
          useBuiltChildQuery: this.useBuiltChildQuery,
        },
        RETRY_AXIOS_OPTION,
      );

      const data = result.data;
      const jobs = data.jobsInfos ?? [];

      handleGetTableFailsIfAny({ jobs, callback: argument.failCallBack });

      const someJobsAreNotFinished = Array.isArray(jobs) && jobs.some(job => job?.status === 'IN_PROCESS');
      if (!someJobsAreNotFinished) {
        // stop calling getWidget
        argument.doneCallBack(data);
        return;
      } else {
        // call again getWidgetGetTableChildren to have fresher data
        if (!data.nothingToRender) {
          argument.reRenderCallBack(data);
        }
        const lastRefreshedTime = Date.now();
        const retryCount = argument.retryCount || 1;

        setTimeout(async () => {
          await this.getChildrenOfTableRow({
            ...argument,
            retryNoCache: false,
            lastRefreshedTime,
            jobIds: jobs?.map(j => j?.jobId),
            retryCount: retryCount + 1,
          });
        }, Math.min(500 * retryCount, 5000));
      }
    } catch (err) {
      if (isRequestCancelled(err)) return;
      argument.failCallBack({ error: err });
    }
  }

  async legacyGetChildrenOfTableRow({
    parentRow,
    dimensionId,
    customMetrics = [],
    widgetId = null,
    reRenderCallBack = null,
    failCallBack = null,
    doneCallBack = null,
    isCustomTable = false,
    forceRefreshCache = false,
    widget = null,
    view = null,
    timeZone = 'Asia/Seoul',
    originalDateFilter = undefined,
  }) {
    if (this.debug) {
      // @ts-ignore
      console.log('getChildrenOfTableRow', arguments[0]);
    }
    const callbacks = {
      reRenderCallBack,
      failCallBack,
      doneCallBack,
    };
    const childQueries = parentRow.childQueries;
    const breakdowns = [dimensionId];
    const currency = this.currency;
    const isMarketingRow = parentRow.dataSourceType === 'marketing';
    // Group ad accounts in sets. This is useful for GA.
    // const fields = getMetricsFromWidget({ widget, customMetrics: this.customMetrics });
    const queries = Object.entries(childQueries)
      .map(([dataSourceName, queryRequest]) => {
        // @ts-ignore
        const newQueryRequest = { ...queryRequest, breakdowns };
        // @ts-ignore
        if (queryRequest.joinedQueries) {
          // @ts-ignore
          const analyticsInfo = getAnalyticsInfo(queryRequest.mainDataSource);
          if (analyticsInfo) {
            let level = breakdowns[0];
            if (!marketingLevels.includes(level)) {
              level = 'adAccount';
            }
            const on = {};
            for (const joinField of analyticsInfo.marketingJoinFieldByLevel[level]) {
              on[joinField] = analyticsInfo.fieldMapping[joinField];
            }
            newQueryRequest.joinedQueries[0].query.breakdowns = Object.values(on);
            newQueryRequest.joinedQueries[0].on = on;
            // @ts-ignore
          } else if (queryRequest?.mainDataSource?.settings) {
            const on = {
              [blendDataUtils.getAdDataKeyFromBreakdown(dimensionId)]: blendDataUtils.getBlendedBreakdownKey({
                // @ts-ignore
                dataSource: queryRequest.mainDataSource,
                queryBreakdown: dimensionId,
              }),
            };
            newQueryRequest.joinedQueries[0].on = on;
          }
          const breakdown = newQueryRequest.breakdowns[0];
          if (dateGroupBreakdowns.includes(breakdown)) {
            newQueryRequest.breakdowns = ['all'];
            newQueryRequest.joinedQueries[0].query.breakdowns.push(breakdown);
          }
        }
        // this code is here because with isGAAutoMatchingEnabled flag we can get an invalid child query if the next breakdown is not compatible
        if (
          isMarketingRow &&
          newQueryRequest.table === 'analytic' &&
          !['all', 'day', 'week', 'month'].includes(dimensionId)
        ) {
          return null;
        }
        return newQueryRequest;
      })
      .filter(Boolean);
    if (queries.length === 0) {
      return [];
    }

    let workspaceId = queries[0].workspaceId;
    const queryResult = await this.getQueryTable({
      queries: queries,
      workspaceId,
      currency,
      customMetrics,
      reRenderCallBack: queryReRenderCallBack.bind(callbacks),
      failCallBack: queryFailCallBack.bind(callbacks),
      doneCallBack: queryDoneCallBack.bind(callbacks),
      parentRow,
      view,
      originalDateFilter,
    });
    return queryResult;
  }

  /**
   *
   * Make a table in the front end format.
   * "args" have the same format than the arguments of getQueryTable.
   * If args.includeTotals is true, the table with have only one row conntaining the total,
   * and the breakdowns will be in its children.
   * For graphs, the final table has one row for each breakdown, and children are for each date.
   *
   *
   */
  async getFinalTable({ totalTable, breakdownTable, args }) {
    const groupBy = totalTable?.groupBy ?? breakdownTable?.groupBy;
    // for graph, we have to group by date first then call the function to get the others row for each date
    if (groupBy !== 'all') {
      const totalRowsByDate = totalTable ? _.groupBy(totalTable.rows, 'from') : {};
      const breakdownRowsByDate = breakdownTable ? _.groupBy(breakdownTable.rows, 'from') : {};
      const dates = Object.keys(breakdownRowsByDate ?? totalRowsByDate);
      const headers = totalTable?.headers ?? breakdownTable?.headers;
      const dimension = breakdownTable?.dimension ?? totalTable?.dimension;

      const rowsById = {};
      // NOTE: Do not use Bluebird.each if you want to do things async!
      // Bluebird.each will process the promises one by one, waiting for each to finish before continuing on the next one
      await Bluebird.map(dates, async date => {
        const totalTable = totalRowsByDate[date]
          ? { rows: totalRowsByDate[date], groupBy: 'all', headers, dimension }
          : null;
        const breakdownTable = breakdownRowsByDate[date]
          ? { rows: breakdownRowsByDate[date], groupBy: 'all', headers, dimension }
          : null;
        const { rows } = await this.getFinalTable({ totalTable, breakdownTable, args });
        for (const row of rows) {
          if (!rowsById[row.id]) {
            rowsById[row.id] = [];
          }
          rowsById[row.id].push(row);
        }
      });
      let rows = Object.entries(rowsById).map(([id, children]) => {
        const name = children?.[0]?.name;
        return {
          id,
          name,
          children,
        };
      });
      const table = { ..._.omit(breakdownTable ?? totalTable, 'rows'), rows };
      return table;
    }
    const finalTable = {
      ...totalTable,
      dataSource: args.queries[0].dataSources[0],
      rows: [],
    };
    if (args.includeTotal) {
      finalTable.dimension = { id: 'all', name: 'all' };
      finalTable.rows = [{ id: 'all', name: 'all', children: [] }];
      if (totalTable) {
        finalTable.rows[0] = _.omit(totalTable.rows[0], ['children', 'childQueries']);
        finalTable.rows[0].children = [];
      }
    }
    if (breakdownTable) {
      let breakdownRows = breakdownTable.rows;
      finalTable.dimension = breakdownTable.dimension;
      if (breakdownTable.warnings) {
        if (finalTable.warnings) {
          finalTable.warnings = finalTable.warnings.concat(breakdownTable.warnings);
        } else {
          finalTable.warnings = breakdownTable.warnings;
        }
      }
      let resultIndicator = 'clicks';
      breakdownRows = breakdownRows.filter(r => r.id !== 'others');
      if (args.sorting) {
        // Do the sorting
        const sign = args.sorting.direction.startsWith('asc') ? 1 : -1;
        if (['results', 'cpr', 'cvr'].includes(args.sorting.field)) {
          resultIndicator = resultIndicatorUtils.getResultIndicator(breakdownRows);
          breakdownRows = _.sortBy(breakdownRows, row => {
            const value =
              row?.cells?.[args.sorting.field] === 'object'
                ? resultIndicatorUtils.getResultMetricFieldValue(args.sorting.field, resultIndicator, row)
                : row?.cells?.[args.sorting.field];
            return sign * value;
          });
        } else {
          breakdownRows = _.sortBy(breakdownRows, row => sign * row?.cells?.[args.sorting.field]);
        }
      }
      if (args?.others?.includeOthers && !args?.others?.skipToCalculateOthers && totalTable) {
        // Make the "others" row containing the sum of all rows that are not displayed.
        const limit = args.others.limit;
        // const needOthers = !limit || breakdownRows.length > limit; we can get less rows than the limit but not getting all the rows with data
        const needOthers = true;
        if (needOthers) {
          if (limit) {
            breakdownRows = breakdownRows.slice(0, limit);
          }
          const othersTable = { ...breakdownTable, rows: [{ ...totalTable.rows[0], children: null }] };
          othersTable.rows[0].id = 'others';
          for (const row of breakdownRows) {
            const otherRow = _.cloneDeep(row);
            for (const metricName in otherRow.cells) {
              if (['results', 'budget'].includes(metricName) && typeof otherRow.cells[metricName] === 'object') {
                const keys = Object.keys(otherRow.cells[metricName]);
                if (metricName === 'results' && keys.length === 0) {
                  otherRow.cells[metricName][resultIndicator] = { [resultIndicator]: -otherRow.cells[resultIndicator] };
                }
                for (const key of keys) {
                  otherRow.cells[metricName][key] = -otherRow.cells[metricName][key];
                }
              } else if (typeof otherRow.cells[metricName] === 'number') {
                otherRow.cells[metricName] = -otherRow.cells[metricName];
              }
            }
            otherRow.id = 'others';
            othersTable.rows.push(otherRow);
          }
          const aggregatedOthersTable = await this.workerUnionTables({
            tables: [othersTable],
            customMetrics: args.customMetrics,
          });
          if (aggregatedOthersTable.rows.length > 1) {
            console.error("'Aggregated Others Table' should have only one row.");
          }
          const othersRow = aggregatedOthersTable.rows[0];
          othersRow.name = 'others';
          othersRow.dimension = breakdownTable.dimension;
          othersRow.childQueries = undefined;
          // remove small values
          const hasPositiveValues = Object.values(othersRow.cells).some(value => value > 0.001);
          for (let metric of Object.keys(othersRow.cells)) {
            const value = othersRow.cells[metric];
            if (typeof value == 'number' && value < 0) {
              delete othersRow.cells[metric];
            }
          }
          // we don't want to add others if there is no positive values inside
          if (hasPositiveValues) {
            if (args.includeTotal) {
              finalTable.rows[0].children.push(othersRow);
            } else {
              finalTable.rows.push(othersRow);
            }
          }
        }
      }
      if (args.includeTotal) {
        finalTable.rows[0].children.unshift(...breakdownRows);
      } else {
        finalTable.rows.unshift(...breakdownRows);
      }
    }
    finalTable.rows = finalTable.rows.filter(row => row.cells || row.children?.length > 0);
    return finalTable;
  }

  getTotalQuery(query) {
    const totalQuery = _.cloneDeep(query);
    totalQuery.breakdowns = ['all'];

    if (query.joinedQueries?.length) {
      totalQuery.joinedQueries.forEach(joinQuery => {
        joinQuery.query.breakdowns = joinQuery.query.breakdowns.filter(
          breakdown => !DATE_BREAKDOWNS.includes(breakdown),
        );
      });
    }
    return totalQuery;
  }

  async getTableWithTotal(args) {
    // Same arguments as getQueryTable
    const that = this;
    // Initialize the table with one row containing the total
    // and the children of that row containing the actual rows.
    let finalTable = null;
    let totalTable = null;
    let breakdownTable = null;
    let isTotalCacheLoaded = false,
      isNonTotalCacheLoaded = false;
    const totalTablePromise = this.getQueryTable({
      ...args,
      queries: args.queries.map(query => that.getTotalQuery(query)),
      async reRenderCallBack({ table, lastUpdatedAt, isCacheLoaded }) {
        totalTable = table;
        finalTable = await that.getFinalTable({ totalTable, breakdownTable, args });
        isTotalCacheLoaded = isCacheLoaded;
        args.reRenderCallBack({
          ...arguments[0],
          table: finalTable,
          lastUpdatedAt,
          isCacheLoaded: isTotalCacheLoaded && isNonTotalCacheLoaded,
        });
      },
      async doneCallBack({ table }) {
        totalTable = table;
      },
    });
    const breakdownTablePromise = this.getQueryTable({
      ...args,
      queries: args.queries.filter(query => !query.withTotal),
      async reRenderCallBack({ table, lastUpdatedAt, isCacheLoaded }) {
        breakdownTable = table;
        finalTable = await that.getFinalTable({ totalTable, breakdownTable, args });
        isNonTotalCacheLoaded = isCacheLoaded;
        args.reRenderCallBack({
          ...arguments[0],
          table: finalTable,
          lastUpdatedAt,
          isCacheLoaded: isTotalCacheLoaded && isNonTotalCacheLoaded,
        });
      },
      async doneCallBack({ table }) {
        breakdownTable = table;
      },
    });
    return await Promise.all([totalTablePromise, breakdownTablePromise])
      .catch(error => {
        console.error(error);
        console.error(args);
        args.failCallBack({
          error,
          args: args?.queries?.[0]?.dataSources?.[0],
          dataSource: args?.queries?.[0]?.dataSources?.[0],
        });
      })
      .then(async jobsInfos => {
        finalTable = await that.getFinalTable({ totalTable, breakdownTable, args });
        if (this.debug) {
          console.log(`getTableWithTotal`, finalTable);
        }
        args.doneCallBack({ table: finalTable, debug: this.debug, jobsInfos });
        return finalTable;
      });
  }
}

module.exports = {
  DashboardQueryClient,
};

function compareJSON(json1, json2) {
  // if both are null or undefined
  if (json1 == null && json2 == null) {
    return;
  }
  if (typeof json1 !== typeof json2) {
    return `typeof ${typeof json1} !== typeof ${typeof json2}`;
  }
  if (!json1 && !json2) {
    return;
  }
  if (typeof json1 == 'boolean' || typeof json2 == 'boolean') {
    if (Boolean(json1) != Boolean(json2)) {
      return `'${json1}'!='${json2}'`;
    }
  } else if (typeof json1 != typeof json2) {
    return `compare ${typeof json1} to ${typeof json2}`;
  }
  if (Array.isArray(json1)) {
    if (!Array.isArray(json2)) {
      return `compare array to ${typeof json2}`;
    } else if (json1.length != json2.length) {
      return `array of diff sizes ${json1.length} and ${json2.length}.`;
    } else {
      let problems = [];
      for (let ind = 0; ind < json1.length; ind++) {
        let prob = compareJSON(json1[ind], json2[ind]);
        if (prob) {
          if (typeof prob === 'object') {
            if (Object.keys(prob).length > 0) {
              problems.push({ ind, prob });
            } else {
              // do nothing
            }
          } else {
            problems.push({ ind, prob });
          }
        }
      }
      if (problems.length > 0) {
        return problems;
      }
    }
  } else if (typeof json1 == 'object') {
    let keys: Set<string> = new Set();
    for (let k of Object.keys(json1)) if (json1[k] != undefined) keys.add(k);
    for (let k of Object.keys(json2)) if (json2[k] != undefined) keys.add(k);
    let problems = {};
    for (let key of keys) {
      try {
        if (key == 'architectureSyncedAt') continue;
        if ((!json1[key] && json2[key]) || (json1[key] && !json2[key])) {
          problems[key] = `Missing key.`;
        } else if (key === 'customMetrics') {
          json1[key] = json1[key]
            .map(elem => elem)
            .sort((a, b) => (a?.id?.toString() || '').localeCompare(b?.id?.toString() || ''));
          json2[key] = json2[key]
            .map(elem => elem)
            .sort((a, b) => (a?.id?.toString() || '').localeCompare(b?.id?.toString() || ''));
          let prob = compareJSON(json1[key], json2[key]);
          if (prob) {
            if (typeof prob === 'object') {
              if (Object.keys(prob).length > 0) {
                problems[key] = prob;
              } else {
                // skip
              }
            } else {
              problems[key] = prob;
            }
          }
        } else {
          let prob = compareJSON(json1[key], json2[key]);
          if (key == 'adAccounts') {
            // let comp = (a, b) => a?.assetId?.toString().localeCompare(b?.assetId?.toString())
            // prob = compareJSON(json1[key].sort(comp), json2[key].sort(comp))

            // NOTE Per: WidgetTable version AdAccount objects are sometimes missing some properties! But both should have the same AdAccountIDs
            // json1[key] = json1[key].map(elem => elem.assetId).sort();
            // json2[key] = json2[key].map(elem => elem.assetId).sort();
            // prob = compareJSON(json1[key], json2[key]);

            // Only compare the list of IDs. The WidgetTable AdAccount objects are sometimes missing some (less important) properties
            const json1KeyCopy = json1[key].map(elem => elem.assetId).sort();
            const json2KeyCopy = json2[key].map(elem => elem.assetId).sort();
            prob = compareJSON(json1KeyCopy, json2KeyCopy);
          }
          if (prob) {
            if (typeof prob === 'object') {
              if (Object.keys(prob).length > 0) {
                problems[key] = prob;
              } else {
                // skip
              }
            } else {
              problems[key] = prob;
            }
          }
        }
      } catch (err) {
        problems[key] = err;
      }
    }
    if (Object.keys(problems).length > 0) {
      return problems;
    }
  } else if (typeof json1 == 'string') {
    if (json1 != json2) {
      return `'${json1}'!='${json2}'`;
    }
  } else if (typeof json1 == 'number') {
    if (
      Math.abs(json1) + Math.abs(json2) > 0.00000001 &&
      (json1 - json2) / (Math.abs(json1) + Math.abs(json2)) > 0.01
    ) {
      return `'${json1}'!='${json2}'`;
    }
  }
}

/**
 * Sends Browser events that can be retrived by Puppeteer.
 * Used to programatically send JS objects from the Browser to Puppeteer, so that it can be saved to disk and used later.
 *
 * For example, save the widget object or the AdAccounts list to be examined later after the run.
 *
 * @param argument Should be the argument sent to GetTable, LegacyGetTable or WidgetTable functions
 */
function sendBrowserEventsToForGetTableFunction(argument: any) {
  let eventAdAccounts = new CustomEvent('PUPPETEER_ADACCOUNTS');
  // @ts-ignore
  eventAdAccounts.pup_prop = {
    args: argument.adAccounts,
    widgetId: argument.widgetId,
    view: argument.view,
  };

  document.dispatchEvent(eventAdAccounts);

  const originalDoneCallback = argument.doneCallBack;
  const newDoneCallBack = async (...args) => {
    // var event = new CustomEvent('PUPPETEER_CALLBACK_FUNC', {
    //   detail: {
    //       args,
    //   },
    // });
    let event = new CustomEvent('PUPPETEER_CALLBACK_FUNC');
    // @ts-ignore
    event.pup_prop = {
      args: args,
      widgetId: argument.widgetId,
      view: argument.view,
    };

    document.dispatchEvent(event);
    originalDoneCallback(...args);
  };
  argument.doneCallBack = newDoneCallBack;

  const originalFailCallBack = argument.failCallBack;
  const newFailCallBack = async (...args) => {
    // var event = new CustomEvent('PUPPETEER_CALLBACK_FUNC', {
    //   detail: {
    //       args,
    //   },
    // });
    let event = new CustomEvent('PUPPETEER_CALLBACK_FUNC_FAIL');
    // @ts-ignore
    event.pup_prop = {
      args: args,
      widgetId: argument.widgetId,
      view: argument.view,
    };

    document.dispatchEvent(event);
    originalFailCallBack(...args);
  };
  argument.failCallBack = newFailCallBack;

  const originalRerenderCallBack = argument.reRenderCallBack;
  const newReRenderCallBack = async (...args) => {
    // var event = new CustomEvent('PUPPETEER_CALLBACK_FUNC', {
    //   detail: {
    //       args,
    //   },
    // });
    let event = new CustomEvent('PUPPETEER_CALLBACK_FUNC_RERENDER');
    // @ts-ignore
    event.pup_prop = {
      args: args,
      widgetId: argument.widgetId,
      view: argument.view,
    };

    document.dispatchEvent(event);
    originalRerenderCallBack(...args);
  };
  argument.reRenderCallBack = newReRenderCallBack;
}

function handleGetTableFailsIfAny({ jobs = [], callback = _.noop }) {
  jobs.forEach(({ status, dataSources, error }) => {
    if (status === 'FAILED') {
      const [dataSource] = dataSources || [];
      const errorResponse = { response: { data: error } };

      callback({ error: errorResponse, dataSource });
    }
  });
}

function lightenView(view) {
  const CONFIG_OMIT_LST = ['widgets', 'viewLayout', 'reportModeLayout', 'styles', 'pdfReportInfo'];

  return !!view
    ? view._isLight // check it is already processed
      ? view
      : {
          ...view,
          config: !!view?.config ? R.omit(CONFIG_OMIT_LST, view.config) : undefined,
          _isLight: true,
        }
    : undefined;
}

function _getMsForRetry(retryCount) {
  const MAX_MS = 7 * 1000;
  const BASE_DELAY = 600;
  const GROWTH_RATE = 1.25;
  const JITTER_PERCENT = 0.1;

  if (retryCount <= 2) {
    return 1000;
  } else if (retryCount >= 30) {
    return MAX_MS;
  }

  const delay = BASE_DELAY * Math.pow(GROWTH_RATE, retryCount);

  const jitterValue = delay * JITTER_PERCENT;
  const jitteredDelay = delay + (Math.random() * (2 * jitterValue) - jitterValue);

  return Math.min(jitteredDelay, MAX_MS);
}
